import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, X, Check, User, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";

interface AdvertiserComboboxProps {
  anuncios: Array<{ apelido?: string | null }>;
  selected: string[];
  onChange: (apelidos: string[]) => void;
  onOpenProfile?: (perfilId: string) => void;
}

const AdvertiserCombobox = ({ anuncios, selected, onChange, onOpenProfile }: AdvertiserComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile && open) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [isMobile, open]);

  const advertisers = useMemo(() => {
    const set = new Set<string>();
    for (const a of anuncios || []) {
      const apelido = (a as any).apelido;
      if (apelido) set.add(apelido);
    }
    return Array.from(set)
      .map(apelido => ({ apelido }))
      .sort((a, b) => a.apelido.localeCompare(b.apelido, "pt-BR"));
  }, [anuncios]);

  const handleOpenProfile = async (apelido: string) => {
    const { data } = await supabase.from("clientes_perfil_public").select("id").eq("apelido", apelido).maybeSingle();
    if (data?.id && onOpenProfile) onOpenProfile(data.id);
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return advertisers;
    const s = search.toLowerCase();
    return advertisers.filter(a => a.apelido.toLowerCase().includes(s));
  }, [advertisers, search]);

  const toggle = (apelido: string) => {
    onChange(selected.includes(apelido) ? selected.filter(s => s !== apelido) : [...selected, apelido]);
  };

  const handleOpen = () => {
    if (open) {
      setOpen(false);
      setSearch("");
      return;
    }
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const listContent = (
    <>
      <div className="px-3 py-2 border-b border-border/40">
        <div className="flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar anunciante..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-transparent font-pixel text-[8px] sm:text-[7px] text-foreground outline-none placeholder:text-muted-foreground/60"
          />
          {search && (
            <button onClick={() => setSearch("")} className="cursor-pointer">
              <X className="w-3 h-3 text-muted-foreground/60" />
            </button>
          )}
        </div>
      </div>

      {selected.length > 0 && (
        <div className="px-3 py-1.5 border-b border-border/40 flex flex-wrap gap-1">
          {selected.map(s => (
            <span key={s} className="flex items-center gap-0.5 bg-primary/20 text-primary px-1.5 py-0.5 rounded-sm font-pixel text-[7px] whitespace-nowrap">
              {s}
              <X className="w-2.5 h-2.5 cursor-pointer hover:text-destructive" onClick={(e) => { e.stopPropagation(); onChange(selected.filter(x => x !== s)); }} />
            </span>
          ))}
        </div>
      )}

      <div className={isMobile ? "flex-1 overflow-auto" : "max-h-[200px] overflow-auto"}>
        {filtered.length === 0 ? (
          <div className="px-3 py-4 font-pixel text-[8px] sm:text-[7px] text-muted-foreground text-center">Nenhum</div>
        ) : (
          filtered.map((adv) => {
            const active = selected.includes(adv.apelido);
            return (
              <div key={adv.apelido} className="w-full flex items-center gap-2 px-3 py-2 sm:py-1.5 font-pixel text-[8px] sm:text-[7px] hover:bg-primary/10 active:bg-primary/20 transition-colors">
                <button onClick={() => toggle(adv.apelido)} className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                  <div className={`w-4 h-4 sm:w-3 sm:h-3 border rounded-sm flex items-center justify-center shrink-0 ${active ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                    {active && <Check className="w-2.5 h-2.5 sm:w-2 sm:h-2 text-primary-foreground" />}
                  </div>
                  <span className={`flex-1 truncate ${active ? "text-primary" : "text-muted-foreground"}`}>{adv.apelido}</span>
                </button>
                {onOpenProfile && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleOpenProfile(adv.apelido); }}
                    className="shrink-0 p-1 sm:p-0.5 hover:text-primary text-muted-foreground/50 hover:scale-110 transition-all cursor-pointer"
                    title="Ver perfil"
                  >
                    <User className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </>
  );

  return (
    <>
      <div ref={ref} className="relative flex flex-col gap-0.5">
        <label className="font-pixel text-[6px] text-muted-foreground flex items-center gap-1">
          <User className="w-2.5 h-2.5" /> Anunciante
        </label>
        <button
          onClick={handleOpen}
          className="flex items-center gap-1 px-2 py-1.5 tibia-border bg-card rounded-sm font-pixel text-[7px] text-muted-foreground cursor-pointer min-w-[120px]"
        >
          <div className="flex-1 flex items-center gap-1 overflow-hidden">
            {selected.length === 0 ? (
              <span className="text-muted-foreground/60">Todos</span>
            ) : (
              <div className="flex items-center gap-0.5 overflow-hidden">
                {selected.slice(0, 1).map(s => (
                  <span key={s} className="flex items-center gap-0.5 bg-primary/20 text-primary px-1 rounded-sm text-[6px] whitespace-nowrap">
                    {s}
                    <X className="w-2 h-2 cursor-pointer hover:text-destructive" onClick={(e) => { e.stopPropagation(); onChange(selected.filter(x => x !== s)); }} />
                  </span>
                ))}
                {selected.length > 1 && <span className="text-[6px] text-primary">+{selected.length - 1}</span>}
              </div>
            )}
          </div>
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && !isMobile && (
          <div className="absolute top-full left-0 mt-1 z-50 tibia-border bg-card rounded-sm py-1 min-w-[200px] shadow-lg">
            <div className="px-2 pb-1 border-b border-border/40">
              <input
                ref={inputRef}
                type="text"
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent font-pixel text-[7px] text-foreground outline-none placeholder:text-muted-foreground/60 py-1"
              />
            </div>
            <div className="max-h-[200px] overflow-auto">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 font-pixel text-[7px] text-muted-foreground text-center">Nenhum</div>
              ) : (
                filtered.map((adv) => {
                  const active = selected.includes(adv.apelido);
                  return (
                    <div key={adv.apelido} className="w-full flex items-center gap-2 px-2 py-1.5 font-pixel text-[7px] hover:bg-primary/10 transition-colors">
                      <button onClick={() => toggle(adv.apelido)} className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                        <div className={`w-3 h-3 border rounded-sm flex items-center justify-center shrink-0 ${active ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                          {active && <Check className="w-2 h-2 text-primary-foreground" />}
                        </div>
                        <span className={`flex-1 truncate ${active ? "text-primary" : "text-muted-foreground"}`}>{adv.apelido}</span>
                      </button>
                      {onOpenProfile && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleOpenProfile(adv.apelido); }}
                          className="shrink-0 p-0.5 hover:text-primary text-muted-foreground/50 hover:scale-110 transition-all cursor-pointer"
                          title="Ver perfil"
                        >
                          <User className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {open && isMobile && (
        <div className="fixed inset-0 z-[200] bg-background flex flex-col">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-border/40">
            <span className="font-pixel text-[9px] text-foreground">Selecionar Anunciante</span>
            <button onClick={() => { setOpen(false); setSearch(""); }} className="font-pixel text-[8px] text-primary cursor-pointer px-2 py-1">
              Fechar
            </button>
          </div>
          {listContent}
        </div>
      )}
    </>
  );
};

export default AdvertiserCombobox;
